import { createTheme, alpha } from '@mui/material/styles';

// Define the base palette with clean, modern colors inspired by Heineken
const PRIMARY = {
  lighter: '#F1F8F4',
  light: '#A8D5B3',
  main: '#5CB85C', // Softer green
  dark: '#449D44',
  darker: '#357A35',
};

const SECONDARY = {
  lighter: '#FFF5F5',
  light: '#FC8181',
  main: '#E53E3E', // Softer red
  dark: '#C53030',
  darker: '#9B2C2C',
};

const SUCCESS = {
  lighter: '#E8F5E9',
  light: '#81C784',
  main: '#4CAF50',
  dark: '#388E3C',
  darker: '#1B5E20',
};

const WARNING = {
  lighter: '#FFF8E1',
  light: '#FFD54F',
  main: '#FFC107',
  dark: '#FFA000',
  darker: '#FF6F00',
};

const ERROR = {
  lighter: '#FFEBEE',
  light: '#EF5350',
  main: '#E40428', // Heineken red (same as secondary)
  dark: '#C4001D',
  darker: '#9B0017',
};

const INFO = {
  lighter: '#E3F2FD',
  light: '#64B5F6',
  main: '#2196F3',
  dark: '#1976D2',
  darker: '#0D47A1',
};

const GREY = {
  0: '#FFFFFF',
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

// Create the premium theme
const premiumTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: PRIMARY,
      secondary: SECONDARY,
      success: SUCCESS,
      warning: WARNING,
      error: ERROR,
      info: INFO,
      grey: GREY,
      common: {
        black: '#000000',
        white: '#FFFFFF',
      },
      text: {
        primary: isDark ? GREY[100] : GREY[800],
        secondary: isDark ? GREY[400] : GREY[600],
        disabled: isDark ? GREY[600] : GREY[500],
      },
      background: {
        paper: isDark ? GREY[800] : GREY[0],
        default: isDark ? GREY[900] : GREY[50],
        neutral: isDark ? GREY[800] : GREY[100],
      },
      action: {
        active: isDark ? GREY[300] : GREY[700],
        hover: alpha(isDark ? GREY[300] : GREY[700], 0.08),
        selected: alpha(isDark ? GREY[300] : GREY[700], 0.16),
        disabled: alpha(isDark ? GREY[300] : GREY[700], 0.38),
        disabledBackground: alpha(isDark ? GREY[300] : GREY[700], 0.12),
        focus: alpha(isDark ? GREY[300] : GREY[700], 0.16),
      },
      divider: alpha(isDark ? GREY[300] : GREY[700], 0.12),
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontWeight: 600,
        lineHeight: 1.2,
        fontSize: '2.5rem',
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 600,
        lineHeight: 1.3,
        fontSize: '2rem',
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 600,
        lineHeight: 1.4,
        fontSize: '1.5rem',
        letterSpacing: '0em',
      },
      h4: {
        fontWeight: 600,
        lineHeight: 1.4,
        fontSize: '1.25rem',
        letterSpacing: '0em',
      },
      h5: {
        fontWeight: 600,
        lineHeight: 1.5,
        fontSize: '1.125rem',
        letterSpacing: '0em',
      },
      h6: {
        fontWeight: 600,
        lineHeight: 1.5,
        fontSize: '1rem',
        letterSpacing: '0em',
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: 1.5,
        fontSize: '1rem',
      },
      subtitle2: {
        fontWeight: 500,
        lineHeight: 1.5,
        fontSize: '0.875rem',
      },
      body1: {
        lineHeight: 1.5,
        fontSize: '1rem',
      },
      body2: {
        lineHeight: 1.5,
        fontSize: '0.875rem',
      },
      caption: {
        lineHeight: 1.5,
        fontSize: '0.75rem',
      },
      overline: {
        fontWeight: 600,
        lineHeight: 1.5,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      },
      button: {
        fontWeight: 500,
        lineHeight: 1.5,
        fontSize: '0.875rem',
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 4,
    },
    shadows: [
      'none',
      `0px 1px 2px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 2px 4px -1px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 1px 6px -1px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 3px 6px -2px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 2px 8px -2px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 4px 8px -2px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 3px 12px -4px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 5px 12px -4px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 4px 16px -4px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 6px 16px -6px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 6px 20px -8px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 7px 20px -6px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 8px 24px -8px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 8px 24px -6px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 10px 28px -10px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 9px 28px -6px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 12px 36px -12px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 10px 32px -6px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 14px 44px -14px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 11px 36px -8px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 16px 52px -16px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      `0px 12px 42px -8px ${alpha(isDark ? '#000' : GREY[900], 0.06)}, 
       0px 20px 60px -20px ${alpha(isDark ? '#000' : GREY[900], 0.08)}`,
      ...Array(12).fill(
        `0px 16px 64px -16px ${alpha(isDark ? '#000' : GREY[900], 0.15)}`
      ),
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              boxShadow: isDark ? `0 2px 8px ${alpha('#000', 0.15)}` : `0 2px 8px ${alpha(GREY[900], 0.1)}`,
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isDark ? `0 2px 8px ${alpha('#000', 0.2)}` : `0 2px 8px ${alpha(GREY[900], 0.15)}`,
            },
          },
          containedPrimary: {
            backgroundColor: PRIMARY.main,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: PRIMARY.dark,
            },
          },
          containedSecondary: {
            backgroundColor: SECONDARY.main,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: SECONDARY.dark,
            },
          },
          outlinedPrimary: {
            borderColor: alpha(PRIMARY.main, 0.5),
            color: PRIMARY.main,
            '&:hover': {
              borderColor: PRIMARY.main,
              backgroundColor: alpha(PRIMARY.main, 0.08),
            },
          },
          outlinedSecondary: {
            borderColor: alpha(SECONDARY.main, 0.5),
            color: SECONDARY.main,
            '&:hover': {
              borderColor: SECONDARY.main,
              backgroundColor: alpha(SECONDARY.main, 0.08),
            },
          },
          sizeSmall: {
            padding: '6px 16px',
            fontSize: '0.8125rem',
          },
          sizeMedium: {
            padding: '10px 20px',
            fontSize: '0.875rem',
          },
          sizeLarge: {
            padding: '12px 24px',
            fontSize: '1rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none',
            backgroundColor: isDark ? GREY[800] : '#FFFFFF',
          },
          elevation1: {
            boxShadow: isDark
              ? `0 1px 3px ${alpha('#000', 0.12)}, 0 1px 2px ${alpha('#000', 0.24)}`
              : `0 1px 3px ${alpha(GREY[900], 0.12)}, 0 1px 2px ${alpha(GREY[900], 0.24)}`,
          },
          elevation2: {
            boxShadow: isDark 
              ? `0 3px 6px ${alpha('#000', 0.15)}, 0 2px 4px ${alpha('#000', 0.12)}`
              : `0 3px 6px ${alpha(GREY[900], 0.15)}, 0 2px 4px ${alpha(GREY[900], 0.12)}`,
          },
          elevation3: {
            boxShadow: isDark
              ? `0 2px 8px ${alpha('#000', 0.15)}, 0 1px 4px ${alpha('#000', 0.12)}`
              : `0 2px 8px ${alpha(GREY[900], 0.1)}, 0 1px 4px ${alpha(GREY[900], 0.06)}`,
          },
          elevation8: {
            boxShadow: isDark
              ? `0 8px 24px ${alpha('#000', 0.16)}`
              : `0 8px 24px ${alpha(GREY[900], 0.12)}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            backgroundImage: 'none',
            boxShadow: isDark
              ? `0 8px 24px ${alpha('#000', 0.2)}`
              : `0 8px 24px ${alpha(GREY[900], 0.12)}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${alpha(isDark ? GREY[500] : GREY[300], 0.3)}`,
            padding: '12px 16px',
          },
          head: {
            color: isDark ? GREY[300] : GREY[700],
            backgroundColor: isDark ? GREY[800] : alpha(PRIMARY.lighter, 0.8),
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontWeight: 500,
            fontSize: '0.75rem',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            alignItems: 'center',
          },
          standardSuccess: {
            backgroundColor: alpha(SUCCESS.main, 0.1),
            color: isDark ? SUCCESS.light : SUCCESS.dark,
          },
          standardInfo: {
            backgroundColor: alpha(INFO.main, 0.1),
            color: isDark ? INFO.light : INFO.dark,
          },
          standardWarning: {
            backgroundColor: alpha(WARNING.main, 0.1),
            color: isDark ? WARNING.light : WARNING.dark,
          },
          standardError: {
            backgroundColor: alpha(ERROR.main, 0.1),
            color: isDark ? ERROR.light : ERROR.dark,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            letterSpacing: 0,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            borderRadius: 2,
            height: 3,
            backgroundColor: SECONDARY.main,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 0,
            padding: '0 16px',
            '&.Mui-selected': {
              fontWeight: 600,
              color: PRIMARY.main,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
            backgroundColor: alpha(PRIMARY.main, 0.12),
          },
          bar: {
            borderRadius: 4,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(isDark ? GREY[300] : GREY[700], 0.2),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(isDark ? GREY[300] : GREY[700], 0.3),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
          notchedOutline: {
            borderRadius: 4,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
            minWidth: 'auto',
            marginRight: 16,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            '&.Mui-selected': {
              backgroundColor: alpha(PRIMARY.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(PRIMARY.main, 0.15),
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            borderColor: alpha(isDark ? GREY[300] : GREY[700], 0.2),
            '&.Mui-selected': {
              backgroundColor: alpha(PRIMARY.main, 0.1),
              color: isDark ? PRIMARY.light : PRIMARY.main,
              '&:hover': {
                backgroundColor: alpha(PRIMARY.main, 0.15),
              },
            },
          },
        },
      },
    },
  });
};

export default premiumTheme; 